import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Home, Building2, MapPin } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { PROPERTY_TYPES } from '../../data/mockData';

const HeroSection = () => {
  const navigate = useNavigate();
  const [searchType, setSearchType] = useState('buy');
  const [searchParams, setSearchParams] = useState({
    keyword: '',
    propertyType: 'all',
    beds: 'any',
    baths: 'any',
    minPrice: 'none',
    maxPrice: 'none',
  });

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchParams.keyword && searchParams.keyword.trim()) {
      params.set('keyword', searchParams.keyword.trim());
    }
    if (searchParams.propertyType && searchParams.propertyType !== 'all') {
      params.set('propertyType', searchParams.propertyType);
    }
    if (searchParams.beds && searchParams.beds !== 'any') {
      params.set('beds', searchParams.beds);
    }
    if (searchParams.baths && searchParams.baths !== 'any') {
      params.set('baths', searchParams.baths);
    }
    if (searchParams.minPrice && searchParams.minPrice !== 'none') {
      params.set('minPrice', searchParams.minPrice);
    }
    if (searchParams.maxPrice && searchParams.maxPrice !== 'none') {
      params.set('maxPrice', searchParams.maxPrice);
    }
    navigate(`/search?${params.toString()}`);
  };

  return (
    <section className="relative min-h-[600px] flex items-center">
      {/* Background */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1920)',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/80" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 py-16">
        <div className="text-center mb-10">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4">
            Find Your <span className="text-red-500">Dream Home</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Discover properties across Ontario
          </p>
        </div>

        {/* Search Box */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
            <Tabs value={searchType} onValueChange={setSearchType} className="w-full">
              <TabsList className="w-full grid grid-cols-2 bg-gray-100 rounded-none h-14">
                <TabsTrigger 
                  value="buy" 
                  className="data-[state=active]:bg-red-600 data-[state=active]:text-white rounded-none h-full text-lg font-medium"
                >
                  <Home className="mr-2" size={20} />
                  Buy a Home
                </TabsTrigger>
                <TabsTrigger 
                  value="sell" 
                  className="data-[state=active]:bg-red-600 data-[state=active]:text-white rounded-none h-full text-lg font-medium"
                >
                  <Building2 className="mr-2" size={20} />
                  Sell a Home
                </TabsTrigger>
              </TabsList>

              <TabsContent value="buy" className="p-6 mt-0">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  <div className="lg:col-span-2">
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Location</label>
                    <div className="relative">
                      <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                      <Input
                        placeholder="Enter city, address, or MLS#"
                        value={searchParams.keyword}
                        onChange={(e) => setSearchParams({...searchParams, keyword: e.target.value})}
                        className="pl-10 h-12"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleSearch();
                          }
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Beds</label>
                    <Select 
                      value={searchParams.beds} 
                      onValueChange={(v) => setSearchParams({...searchParams, beds: v})}
                    >
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Any" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any</SelectItem>
                        {[1, 2, 3, 4, 5].map((num) => (
                          <SelectItem key={num} value={num.toString()}>{num}+</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Baths</label>
                    <Select 
                      value={searchParams.baths} 
                      onValueChange={(v) => setSearchParams({...searchParams, baths: v})}
                    >
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Any" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any</SelectItem>
                        {[1, 2, 3, 4, 5].map((num) => (
                          <SelectItem key={num} value={num.toString()}>{num}+</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Property Type</label>
                    <Select 
                      value={searchParams.propertyType} 
                      onValueChange={(v) => setSearchParams({...searchParams, propertyType: v})}
                    >
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="All Types" />
                      </SelectTrigger>
                      <SelectContent>
                        {PROPERTY_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Min Price</label>
                    <Select 
                      value={searchParams.minPrice} 
                      onValueChange={(v) => setSearchParams({...searchParams, minPrice: v})}
                    >
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="No Min" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Min</SelectItem>
                        {[200000, 300000, 400000, 500000, 600000, 700000, 800000, 1000000, 1500000].map((price) => (
                          <SelectItem key={price} value={price.toString()}>
                            ${price.toLocaleString()}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">Max Price</label>
                    <Select 
                      value={searchParams.maxPrice} 
                      onValueChange={(v) => setSearchParams({...searchParams, maxPrice: v})}
                    >
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="No Max" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Max</SelectItem>
                        {[300000, 400000, 500000, 600000, 700000, 800000, 1000000, 1500000, 2000000, 3000000].map((price) => (
                          <SelectItem key={price} value={price.toString()}>
                            ${price.toLocaleString()}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button 
                  onClick={handleSearch}
                  className="w-full mt-6 h-14 bg-red-600 hover:bg-red-700 text-white text-lg font-semibold"
                >
                  <Search className="mr-2" size={20} />
                  Search Properties
                </Button>
              </TabsContent>

              <TabsContent value="sell" className="p-6 mt-0">
                <div className="text-center py-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">Ready to Sell Your Home?</h3>
                  <p className="text-gray-600 mb-6">Get a free home evaluation and discover what your property is worth in today's market.</p>
                  <Button 
                    onClick={() => navigate('/sell')}
                    className="h-14 px-8 bg-red-600 hover:bg-red-700 text-white text-lg font-semibold"
                  >
                    Get Your Free Evaluation
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
